import * as THREE from 'three';
import { COLLISION_MODE } from '../loaders/zsc';

/**
 * CollisionDetector - Handles collision detection between characters and objects
 * Uses THREE.js raycasting for precise mesh-based collision detection
 */
export class CollisionDetector {
    private getColObjects: () => THREE.Object3D[];
    private raycaster: any;
    private rayDirection: any;
    private rayOrigin: any;

    constructor(getColObjects: () => THREE.Object3D[]) {
        this.getColObjects = getColObjects;
        this.raycaster = new THREE.Raycaster();
        this.rayDirection = new THREE.Vector3();
        this.rayOrigin = new THREE.Vector3();
    }

    /**
     * Check if a character can move to a new position without colliding with obstacles
     * Uses raycasting for precise mesh-based collision detection
     * @param character - The character object
     * @param newPosition - The target position to check
     * @returns true if movement is allowed, false if blocked
     */
    checkCollision(character: any, newPosition: any): boolean {
        const currentPos = character.position;

        // Calculate movement direction and distance
        this.rayDirection.set(
            newPosition.x - currentPos.x,
            newPosition.y - currentPos.y,
            0 // Only check horizontal collision
        );

        const moveDistance = this.rayDirection.length();
        if (moveDistance < 0.001) {
            return true; // No movement, no collision
        }

        this.rayDirection.normalize();

        // Get collision objects to check
        const colObjects = this._getCollisionMeshes();
        if (colObjects.length === 0) {
            return true;
        }

        // Cast rays at multiple heights to cover the character's body
        // Heights: mid-body and head level (skip feet to allow stepping over small obstacles)
        const rayHeights = [0.8, 1.3]; // Skip low rays to allow step-over
        const collisionRadius = 0.5; // Character's collision radius

        for (let i = 0; i < rayHeights.length; i++) {
            const height = rayHeights[i];

            // Cast ray from character center
            this.rayOrigin.set(
                currentPos.x,
                currentPos.y,
                currentPos.z + height
            );

            this.raycaster.set(this.rayOrigin, this.rayDirection);
            this.raycaster.far = moveDistance + collisionRadius;

            const intersects = this.raycaster.intersectObjects(colObjects, false);

            if (intersects.length > 0) {
                const hit = intersects[0];

                // Check if the hit is within collision range
                if (hit.distance <= moveDistance + collisionRadius) {
                    return false; // Collision detected
                }
            }
        }

        return true; // No collision
    }

    /**
     * Get all meshes that should block horizontal movement.
     *
     * Two object categories are handled:
     *  1. ZSC-loaded map objects (COLL_, CNST_, DECO_ name prefix):
     *     filtered by the per-mesh `collisionMode` property set by the ZSC loader.
     *  2. Editor-placed objects that have `userData.collision.mode` set:
     *     included unless their mode is HEIGHT_ONLY (terrain-only, no wall blocking).
     */
    private _getCollisionMeshes(): any[] {
        const meshes: any[] = [];
        const colObjects = this.getColObjects();

        for (let i = 0; i < colObjects.length; i++) {
            const colObj = colObjects[i];

            const isZscObject =
                colObj.name &&
                (colObj.name.startsWith("COLL_") ||
                 colObj.name.startsWith("CNST_") ||
                 colObj.name.startsWith("DECO_"));

            if (isZscObject) {
                // ZSC path: per-mesh collisionMode from ZSC loader.
                colObj.traverse((child: any) => {
                    if (child.collisionMode === COLLISION_MODE.NONE) return;
                    if (child.isMesh) meshes.push(child);
                });
                continue;
            }

            // Editor path: object has an explicit collision mode in userData.
            const editorMode: number = colObj.userData?.collision?.mode ?? 0;
            if (editorMode === 0) continue;

            // HEIGHT_ONLY objects only affect terrain snapping, not horizontal blocking.
            if (editorMode & COLLISION_MODE.HEIGHT_ONLY) continue;

            colObj.traverse((child: any) => {
                if (child.isMesh) meshes.push(child);
            });
        }

        return meshes;
    }

    /**
     * Get objects that might collide with a position (for optimization)
     * Uses distance-based culling to reduce checks
     */
    getNearbyCollisionObjects(position: any, radius: number): any[] {
        const nearby: any[] = [];
        const radiusSq = radius * radius;
        const colObjects = this.getColObjects();

        for (let i = 0; i < colObjects.length; i++) {
            const colObj = colObjects[i];

            // Check COLL_, CNST_, and DECO_ objects
            if (!colObj.name ||
                !(colObj.name.startsWith("COLL_") ||
                  colObj.name.startsWith("CNST_") ||
                  colObj.name.startsWith("DECO_"))) {
                continue;
            }

            // Quick distance check using object position
            const objPos = colObj.position || (colObj.getWorldPosition && colObj.getWorldPosition(new THREE.Vector3()));
            if (objPos) {
                const dx = objPos.x - position.x;
                const dy = objPos.y - position.y;
                const distSq = dx * dx + dy * dy;

                if (distSq <= radiusSq) {
                    nearby.push(colObj);
                }
            }
        }

        return nearby;
    }
}
